import axios from "axios";
import { API } from "./helpers";

const ERROR_EXPLANATIONS = {
  400: "The request was invalid — likely missing or malformed input.",
  401: "The user's session expired or they aren't logged in.",
  403: "The user tried to access a feature they don't have permission for.",
  404: "The requested resource (page, property, or data) was not found.",
  429: "Too many requests — the user hit a rate limit.",
  500: "A server-side error occurred — likely a backend bug or failed external API call.",
  502: "The server received a bad response from an upstream service.",
  503: "The service is temporarily unavailable — could be maintenance or overload.",
  network: "Network error — the user may have lost internet or the server is unreachable.",
  render: "A React component crashed during rendering.",
  unhandled: "An uncaught JavaScript error occurred in the user's browser.",
};

// Known browser extension noise patterns — skip logging these
const EXTENSION_NOISE = [
  "Object Not Found Matching Id",
  "MethodName:update",
  "ResizeObserver loop",
  "this.o.at is not a function",
  "Extension context invalidated",
  "chrome-extension://",
  "moz-extension://",
];

function isExtensionNoise(message) {
  if (!message) return false;
  return EXTENSION_NOISE.some((pattern) => message.includes(pattern));
}

/**
 * Browser-internal noise patterns that surface as window-level errors but
 * originate from the browser itself (autofill controllers, smart-select
 * popovers, password manager bridges) — not from our code. We can't fix
 * them, the user can't either, and they're not actionable for us, so we
 * drop them from the log to keep signal-to-noise high.
 *
 * Each entry is a substring match against `event.message`. Add new entries
 * as you spot them in the error log; do NOT remove existing entries
 * unless you've confirmed the underlying browser regression is gone.
 */
const BROWSER_INTERNAL_NOISE = [
  "_AutofillCallbackHandler",       // iOS Safari native autofill controller
  "InstallTrigger",                 // Firefox extension API leaking
  "__firefox__",                    // Firefox internal namespace
  "webkit.messageHandlers",         // Safari WKWebView bridge
  "MutationObserver was disconnected",  // browser-internal observer cleanup
];

function isBrowserInternalNoise(message) {
  if (!message) return false;
  const m = String(message);
  return BROWSER_INTERNAL_NOISE.some((needle) => m.includes(needle));
}


/**
 * Cross-origin browser script errors (third-party scripts loaded from
 * different domains — analytics, social embeds, ad networks, etc.) are
 * sandboxed by the browser. Their `event.message` collapses to the literal
 * string "Script error." with no filename, line, column, or stack trace
 * because the spec hides those details to prevent CSRF-style information
 * leaks. They are not actionable — we cannot debug them without source
 * access — so we drop them from our error log to keep signal-to-noise high.
 *
 * https://developer.mozilla.org/en-US/docs/Web/API/GlobalEventHandlers/onerror
 */
function isCrossOriginScriptError(event) {
  if (!event) return false;
  const msg = (event.message || "").trim();
  if (!msg.startsWith("Script error")) return false;
  // Cross-origin errors arrive with NO filename / line / column AND no
  // `error` object. If any of those are populated, it's a first-party error
  // whose message HAPPENS to start with "Script error" — keep logging it.
  const hasFile = Boolean(event.filename);
  const hasError = Boolean(event.error);
  return !hasFile && !hasError;
}

function getExplanation(errorType, statusCode, message) {
  if (statusCode && ERROR_EXPLANATIONS[statusCode]) {
    return ERROR_EXPLANATIONS[statusCode];
  }
  if (errorType === "network_error") return ERROR_EXPLANATIONS.network;
  if (errorType === "render_error") return ERROR_EXPLANATIONS.render;
  if (errorType === "unhandled_error") return ERROR_EXPLANATIONS.unhandled;
  if (message?.includes("timeout")) return "The request timed out — the server took too long to respond.";
  if (message?.includes("CORS")) return "Cross-origin request blocked — possible misconfiguration.";
  return `An unexpected error occurred: ${message || "unknown"}`;
}

export function logError({ errorType, message, page, url, statusCode, endpoint, stack }) {
  // Skip browser extension noise
  if (isExtensionNoise(message) || isExtensionNoise(stack)) return;

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const explanation = getExplanation(errorType, statusCode, message);

  axios.post(`${API}/error-log`, {
    error_type: errorType || "unknown",
    message: message || "",
    page: page || window.location.pathname,
    url: url || window.location.href,
    user_id: user.id || "anonymous",
    user_email: user.email || "",
    status_code: statusCode || null,
    endpoint: endpoint || "",
    stack: stack ? stack.substring(0, 1000) : "",
    explanation,
  }).catch(() => {});
}

// ─────────────────────────────────────────────────────────────────────────────
// Network-error sliding-window detector
// ─────────────────────────────────────────────────────────────────────────────
// Goal: trip a "connection issue" toast only AFTER repeated network failures
// within a short window — not on a single transient failure. Reset the
// counter on the next successful axios response so flaky 1-off errors stay
// silent.
//
// CustomEvents (consumed by <NetworkOfflineToast />):
//   - "realix:network-down"   → show banner
//   - "realix:network-up"     → hide banner
const NETWORK_WINDOW_MS = 30_000;
const NETWORK_FAIL_THRESHOLD = 3;
const networkFailures = [];   // array of timestamps (ms)
let networkBannerShown = false;
// Tracks the FIRST failure timestamp of the current outage and the running
// failure count, so that on recovery we can emit a single analytics event
// with `error_count` and `duration_ms`. This distinguishes a real API
// outage (high count, long duration) from flaky user wifi (low count,
// short duration) when reading aggregates in the admin dashboard.
let outageStartedAt = null;
let outageErrorCount = 0;

function fireNetworkRecoveredAnalytics(errorCount, durationMs) {
  if (typeof window === "undefined") return;

  const payload = {
    error_count: errorCount,
    duration_ms: durationMs,
    current_path: window.location.pathname,
    timestamp: new Date().toISOString(),
  };

  // 1) Internal pipeline → Mongo (tied into our existing analytics pipeline).
  //    Fire-and-forget — never throw if the endpoint is unreachable, the
  //    user just got their connection back and we don't want to retrigger
  //    the offline banner with another network failure.
  axios.post(`${API}/analytics/network-recovered`, payload).catch(() => {});

  // 2) GA4 / window.gtag — only if the host page has it loaded. Use the
  //    same field names so funnels can be built across both pipelines.
  try {
    if (typeof window.gtag === "function") {
      window.gtag("event", "network_recovered", payload);
    }
  } catch (_) {
    // gtag itself can throw if a 3rd-party blocker is in the middle of
    // its lifecycle — never let analytics break recovery.
  }
}

function recordNetworkFailure() {
  const now = Date.now();
  // Drop expired entries from the front of the window
  while (networkFailures.length && now - networkFailures[0] > NETWORK_WINDOW_MS) {
    networkFailures.shift();
  }
  networkFailures.push(now);

  // Outage bookkeeping for the recovery analytics event
  if (outageStartedAt === null) {
    outageStartedAt = now;
    outageErrorCount = 0;
  }
  outageErrorCount += 1;

  if (networkFailures.length >= NETWORK_FAIL_THRESHOLD && !networkBannerShown) {
    networkBannerShown = true;
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("realix:network-down"));
    }
  }
}

function recordNetworkSuccess() {
  networkFailures.length = 0;
  if (networkBannerShown) {
    networkBannerShown = false;
    // Snapshot before reset so the analytics payload reflects this outage
    const errorCount = outageErrorCount;
    const durationMs = outageStartedAt ? Date.now() - outageStartedAt : 0;
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("realix:network-up"));
    }
    // Only emit when the banner was actually shown — single transient
    // failures that never tripped the threshold don't generate noise.
    fireNetworkRecoveredAnalytics(errorCount, durationMs);
  }
  // Clear outage tracking on every success — even silent ones — so the
  // next outage starts with a fresh `outageStartedAt` window.
  outageStartedAt = null;
  outageErrorCount = 0;
}

export function setupGlobalErrorHandlers() {
  // Catch unhandled promise rejections
  window.addEventListener("unhandledrejection", (event) => {
    const error = event.reason;
    const message = error?.message || String(error);

    // Skip browser extension noise entirely
    if (isExtensionNoise(message)) return;
    // Skip browser-internal controller noise (iOS Safari autofill, etc.)
    if (isBrowserInternalNoise(message)) return;

    logError({
      errorType: "unhandled_promise",
      message,
      stack: error?.stack,
    });
  });

  // Catch uncaught errors
  window.addEventListener("error", (event) => {
    // Skip third-party "Script error." (no filename, no error object —
    // browser sandbox hides cross-origin details, the message is not
    // actionable). Suppresses these from our logger ONLY; the browser
    // console still receives them so devs can debug locally if needed.
    if (isCrossOriginScriptError(event)) return;

    // Skip browser-internal noise (iOS Safari autofill, Firefox extension
    // bridge, WKWebView messageHandlers) — these are the browser's own
    // controllers leaking exceptions, not first-party bugs.
    if (isBrowserInternalNoise(event.message)) return;

    // Skip browser extension noise
    if (isExtensionNoise(event.message)) return;
    // Skip errors from extension scripts
    if (event.filename && (event.filename.includes("extension") || event.filename.includes("chrome-extension"))) return;

    logError({
      errorType: "unhandled_error",
      message: event.message,
      stack: event.error?.stack,
    });
  });
}

export function setupAxiosErrorInterceptor(onSessionExpired) {
  // Successful response → clear any active network-down banner
  axios.interceptors.response.use(
    (response) => {
      recordNetworkSuccess();
      return response;
    },
    (error) => {
      const status = error.response?.status;
      const url = error.config?.url || "";
      const endpoint = url.replace(/^https?:\/\/[^/]+/, "");

      // True network errors have no `response` (request never reached server)
      // — these are the ones that should trip the offline banner.
      const isNetworkError = !error.response;
      if (isNetworkError) {
        recordNetworkFailure();
      } else {
        // The server responded with SOMETHING, even if it was a 5xx — that
        // proves connectivity is fine. Reset the network-down counter so a
        // burst of 500s doesn't accidentally show the offline banner.
        recordNetworkSuccess();
      }

      // Handle 401s: notify user, don't log. Session lives in the
      // httpOnly cookie now — no localStorage token to clear.
      if (status === 401) {
        if (!endpoint.includes("/auth/login")) {
          // Best-effort cleanup of any legacy token from the previous rollout.
          localStorage.removeItem("session_token");
          localStorage.removeItem("user");
          if (onSessionExpired) onSessionExpired();
        }
        return Promise.reject(error);
      }
      // Skip 403s on subscription checks
      if (status === 403 && (endpoint.includes("/subscription") || endpoint.includes("/saved-"))) {
        return Promise.reject(error);
      }
      // Skip logging for the error-log endpoint itself
      if (endpoint.includes("/error-log")) {
        return Promise.reject(error);
      }
      // Per-call opt-out: callers that perform an "expected probe" (try
      // endpoint A, fall back to endpoint B if A 404s) can pass
      // `{ __suppressErrorLog: true }` in the axios config to keep their
      // intentional misses out of the error log. This is what
      // `DealPage.jsx` does for its `/shared-deals/{slug}` fallback —
      // a 404 there is the EXPECTED outcome of the fallback chain, not
      // a bug, so we shouldn't pollute the error log with it.
      if (error.config?.__suppressErrorLog) {
        return Promise.reject(error);
      }

      logError({
        errorType: status ? `api_${status}` : "network_error",
        message: error.response?.data?.detail || error.message,
        statusCode: status,
        endpoint,
      });
      return Promise.reject(error);
    }
  );
}

// Exposed for tests + the offline toast component.
export const __networkDetector = {
  recordNetworkFailure,
  recordNetworkSuccess,
  isCrossOriginScriptError,
  isBrowserInternalNoise,
  fireNetworkRecoveredAnalytics,
  WINDOW_MS: NETWORK_WINDOW_MS,
  THRESHOLD: NETWORK_FAIL_THRESHOLD,
};
